/**
 * Usage Analytics Component
 * 
 * Displays comprehensive usage analytics including:
 * - Token consumption over time (interactive charts)
 * - Cost breakdown by model type
 * - Current usage and remaining quotas
 * 
 * Implements Requirements: 5.1, 5.3, 5.4, 5.5
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import quotaAnalyticsService, {
  UsageAnalytics as UsageAnalyticsData,
  QuotaStatus,
  ChartData
} from '../../services/QuotaAnalyticsService';
import UsageChart from './UsageChart';
import QuotaDisplay from './QuotaDisplay';
import CostBreakdown from './CostBreakdown';
import styles from './UsageAnalytics.module.css';
import logger from '../../logging/logger';

type TimeRange = 'day' | 'week' | 'month';

export const UsageAnalytics: React.FC = () => {
  const { userUuid, getToken } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('week');
  
  // Data state
  const [usageData, setUsageData] = useState<UsageAnalyticsData | null>(null);
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null);
  const [usageChartData, setUsageChartData] = useState<ChartData | null>(null);
  const [costChartData, setCostChartData] = useState<ChartData | null>(null);
  
  // Prevent duplicate concurrent fetches from React re-renders
  const isFetchingRef = useRef(false);

  // Fetch all analytics data
  const fetchAnalyticsData = useCallback(async () => {
    if (!userUuid) {
      setLoading(false);
      setError('User not authenticated. Please log in to view analytics.');
      return;
    }
    
    // Guard against duplicate concurrent fetches
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Fetch all data in parallel
      const [usage, allTimeUsage, quota, usageChart, costChart] = await Promise.all([
        quotaAnalyticsService.getUsageAnalytics(userUuid, token, timeRange),
        quotaAnalyticsService.getUsageAnalytics(userUuid, token, 'total'),
        quotaAnalyticsService.getQuotaStatus(userUuid, token),
        quotaAnalyticsService.getUsageChartData(userUuid, token, timeRange, 'line'),
        quotaAnalyticsService.getCostChartData(userUuid, token, timeRange, 'model')
      ]);

      setUsageData(allTimeUsage || usage);
      setQuotaStatus(quota);
      setUsageChartData(usageChart);
      setCostChartData(costChart);
    } catch (err) {
      logger.error('Error fetching analytics:', err);
      setError('Failed to load analytics data. Please check if the backend is running.');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userUuid, timeRange]); // Removed getToken - it's stable enough and causes re-renders

  // Fetch data on mount and when time range changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Auto-refresh every 30 seconds (only if we have data)
  useEffect(() => {
    if (!userUuid || error) return;
    const interval = setInterval(fetchAnalyticsData, 30000);
    return () => clearInterval(interval);
  }, [fetchAnalyticsData, userUuid, error]);

  // Show loading state
  if (loading && !usageData && !error) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <p>{error}</p>
          <button onClick={fetchAnalyticsData} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header with time range selector */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Usage Analytics</h2>
          {quotaStatus?.tier?.toLowerCase() === 'developer' && (
            <span className={styles.developerBadge}>Developer Mode</span>
          )}
        </div>
        <div className={styles.timeRangeSelector}>
          <button
            className={`${styles.timeRangeButton} ${timeRange === 'day' ? styles.active : ''}`}
            onClick={() => setTimeRange('day')}
          >
            24h
          </button>
          <button
            className={`${styles.timeRangeButton} ${timeRange === 'week' ? styles.active : ''}`}
            onClick={() => setTimeRange('week')}
          >
            7d
          </button>
          <button
            className={`${styles.timeRangeButton} ${timeRange === 'month' ? styles.active : ''}`}
            onClick={() => setTimeRange('month')}
          >
            30d
          </button>
        </div>
      </div>

      {/* Quota Status Section */}
      {quotaStatus && (
        <QuotaDisplay quotaStatus={quotaStatus} />
      )}

      {/* Usage Summary Cards */}
      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12z"/>
              <path d="M10 5a1 1 0 011 1v3.586l2.707 2.707a1 1 0 01-1.414 1.414l-3-3A1 1 0 019 10V6a1 1 0 011-1z"/>
            </svg>
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Total Tokens</span>
            <span className={styles.cardValue}>
              {usageData ? quotaAnalyticsService.formatTokens(usageData.total_tokens) : '0'}
            </span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
            </svg>
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Total Cost</span>
            <span className={styles.cardValue}>
              {usageData ? quotaAnalyticsService.formatCost(usageData.total_cost) : '$0.00'}
            </span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
            </svg>
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Requests</span>
            <span className={styles.cardValue}>
              {usageData?.total_requests || 0}
            </span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
            </svg>
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Tier</span>
            <span className={styles.cardValue}>
              {quotaStatus?.tier || 'Free'}
            </span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className={styles.chartsSection}>
        {/* Usage Over Time Chart */}
        <div className={styles.chartContainer}>
          <h3 className={styles.chartTitle}>Token Usage Over Time</h3>
          {usageChartData ? (
            <UsageChart chartData={usageChartData} />
          ) : (
            <div className={styles.noData}>
              <p>No usage data available for this period</p>
            </div>
          )}
        </div>

        {/* Cost Breakdown Chart - Developer Tier Only */}
        {quotaStatus?.tier?.toLowerCase() === 'developer' && (
          <div className={styles.chartContainer}>
            <h3 className={styles.chartTitle}>Cost Breakdown by Model</h3>
            {costChartData && Object.keys(costChartData.datasets[0]?.data || {}).length > 0 ? (
              <CostBreakdown chartData={costChartData} />
            ) : (
              <div className={styles.noData}>
                <p>No cost data available for this period</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Usage by Model/Agent Tables */}
      {usageData && (
        <div className={styles.tablesSection}>
          {/* Usage by Model */}
          {usageData.usage_by_model && Object.keys(usageData.usage_by_model).length > 0 && (
            <div className={styles.tableContainer}>
              <h3 className={styles.tableTitle}>Usage by Model</h3>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Tokens</th>
                    <th>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(usageData.usage_by_model).map(([model, tokens]) => (
                    <tr key={model}>
                      <td>{model}</td>
                      <td>{quotaAnalyticsService.formatTokens(tokens)}</td>
                      <td>
                        {quotaAnalyticsService.formatPercentage(
                          (tokens / usageData.total_tokens) * 100
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Usage by Agent */}
          {usageData.usage_by_agent && Object.keys(usageData.usage_by_agent).length > 0 && (
            <div className={styles.tableContainer}>
              <h3 className={styles.tableTitle}>Usage by Agent</h3>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Tokens</th>
                    <th>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(usageData.usage_by_agent).map(([agent, tokens]) => (
                    <tr key={agent}>
                      <td>{agent}</td>
                      <td>{quotaAnalyticsService.formatTokens(tokens)}</td>
                      <td>
                        {quotaAnalyticsService.formatPercentage(
                          (tokens / usageData.total_tokens) * 100
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Refresh indicator */}
      {loading && usageData && (
        <div className={styles.refreshIndicator}>
          <div className={styles.miniSpinner}></div>
          <span>Refreshing...</span>
        </div>
      )}
    </div>
  );
};

export default UsageAnalytics;
